import {rememberReturn} from '../navigation/nativeReturn';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { notificationPath, notificationPreferenceForType, readNotificationPreferences } from './notificationPreferences';

export class NotificationService {
  private static instance: NotificationService;
  private listenersReady?: Promise<void>;
  private registering = false;
  private registered = false;
  private token?: string;
  private uid?: string;
  private tokenWrites=new Set<Promise<void>>();
  private identityChanges=Promise.resolve();
  private queueIdentity(work:()=>Promise<void>){const task=this.identityChanges.then(work);this.identityChanges=task.catch(()=>{});return task;}
  private notificationId = Date.now() % 2147483647;
  private pendingPath?: string;
  private installationId() {
    let id = localStorage.getItem('finflow_notification_device');
    if (!id) { id = crypto.randomUUID(); localStorage.setItem('finflow_notification_device', id); }
    return id;
  }
  static getInstance() { return this.instance || (this.instance = new NotificationService()); }
  private nextNotificationId() { return this.notificationId = (this.notificationId % 2147483646) + 1; }
  private allowed(type?: string) {
    const key = type && notificationPreferenceForType[type];
    return !key || readNotificationPreferences()[key];
  }
  setUser(uid?: string) {return this.queueIdentity(async()=>{
    if (this.uid && this.uid !== uid) await this.clearInstallation();
    this.uid = uid;
    if (uid && this.token) await this.saveToken();
    if (uid && this.pendingPath) { this.navigate(this.pendingPath); this.pendingPath = undefined; }
  });}
  clearUser(){return this.queueIdentity(()=>this.clearInstallation());}
  private async clearInstallation() {
    // Stop new writes before waiting for existing writes, then remove the old installation.
    const oldUid=this.uid;
    this.uid=undefined;this.token=undefined;this.pendingPath=undefined;
    await Promise.allSettled(Array.from(this.tokenWrites));
    if(oldUid){try{await deleteDoc(doc(db,'users',oldUid,'settings',`push-${this.installationId()}`));}catch{/* OS unregister is still attempted. */}}
    if (Capacitor.isNativePlatform()) {
      // Each cleanup must run even if another native plugin fails.
      await Promise.allSettled([
        this.cancelAllNotifications(),
        LocalNotifications.removeAllDeliveredNotifications(),
        ...(Capacitor.getPlatform() === 'android'
          ? [FirebaseMessaging.removeAllDeliveredNotifications(), FirebaseMessaging.deleteToken()]
          : [PushNotifications.removeAllDeliveredNotifications(), PushNotifications.unregister()])
      ]);
      this.registered = false;
    }
  }
  private async saveToken() {
    if (!this.uid || !this.token) return;
    const write=setDoc(doc(db, 'users', this.uid, 'settings', `push-${this.installationId()}`), {
      token: this.token, transport: Capacitor.getPlatform()==='ios'?'apns':'fcm', schemaVersion:1, platform: Capacitor.getPlatform(), preferences: readNotificationPreferences(), updatedAt: new Date()
    });
    this.tokenWrites.add(write);try{await write;}finally{this.tokenWrites.delete(write);}
  }
  async preferencesChanged() {
    const { notifications } = await LocalNotifications.getPending();
    const disabled = notifications.filter(n => !this.allowed(n.extra?.type));
    if (disabled.length) await LocalNotifications.cancel({ notifications: disabled });
    if (this.uid && this.token) await this.saveToken();
  }
  private navigate(path: string) {
    window.dispatchEvent(new CustomEvent('finflow-notification-open', { detail: { path } }));
  }
  private handleTap(data?: unknown) {
    // Version 1 permits type-based routing only; arbitrary URL/account/record fields are ignored.
    const type = data && typeof data === 'object' && 'type' in data && typeof data.type === 'string' ? data.type : undefined;
    const path = notificationPath(type);
    rememberReturn(path);
    if (!this.uid) { this.pendingPath = path; sessionStorage.setItem('finflow_post_login_redirect', path); }
    else this.navigate(path);
  }
  private async setupListeners() {
    await LocalNotifications.addListener('localNotificationActionPerformed', event => this.handleTap(event.notification.extra));
    if (Capacitor.getPlatform() === 'android') {
      await FirebaseMessaging.addListener('tokenReceived', event => {
        this.token = event.token;
        void this.saveToken().catch(() => {});
      });
      await FirebaseMessaging.addListener('notificationActionPerformed', event => this.handleTap(event.notification.data));
      await FirebaseMessaging.addListener('notificationReceived', event => {
        const notification = event.notification;
        if (!this.uid || !this.allowed((notification.data as any)?.type)) return;
        void this.scheduleLocalNotification(notification.title || 'FinFlow', notification.body || 'You have an update.', notification.data).catch(() => {});
      });
      return;
    }
    await PushNotifications.addListener('registration', token => {
      this.token = token.value; this.registered = true; this.registering = false;
      void this.saveToken().catch(() => { /* Retried on next authenticated activation. */ });
    });
    await PushNotifications.addListener('registrationError', () => { this.registering = false; this.registered = false; });
    await PushNotifications.addListener('pushNotificationActionPerformed', event => this.handleTap(event.notification.data));
    await PushNotifications.addListener('pushNotificationReceived', notification => {
      if (!this.uid || !this.allowed(notification.data?.type)) return;
      void this.scheduleLocalNotification(notification.title || 'FinFlow', notification.body || 'You have an update.', notification.data)
        .catch(() => {});
    });
  }
  async initialize(requestPermission = false) {
    if (!Capacitor.isNativePlatform()) return;
    if (!this.listenersReady) this.listenersReady = this.setupListeners().catch(error => { this.listenersReady = undefined; throw error; });
    await this.listenersReady;
    const local = requestPermission ? await LocalNotifications.requestPermissions() : await LocalNotifications.checkPermissions();
    if (local.display !== 'granted') return;
    if (Capacitor.getPlatform() === 'android') {
      await LocalNotifications.createChannel({ id: 'finflow-updates', name: 'FinFlow updates', importance: 4, visibility: 0 });
      await FirebaseMessaging.createChannel({ id: 'finflow-updates', name: 'FinFlow updates', importance: 4, visibility: 0 });
    }
    if (Capacitor.getPlatform() === 'android') {
      if (this.uid && !this.registering) {
        this.registering = true;
        try { this.token = (await FirebaseMessaging.getToken()).token; await this.saveToken(); }
        finally { this.registering = false; }
      }
      return;
    }
    const push = requestPermission ? await PushNotifications.requestPermissions() : await PushNotifications.checkPermissions();
    if (this.uid && push.receive === 'granted' && !this.registering) {
      this.registering = true;
      try { await PushNotifications.register(); } catch (error) { this.registering = false; throw error; }
    }
  }
  async scheduleLocalNotification(title: string, body: string, data?: any, schedule?: any) {
    if (!Capacitor.isNativePlatform() || !this.allowed(data?.type)) return;
    const permission = await LocalNotifications.checkPermissions();
    if (permission.display !== 'granted') throw new Error('Enable notifications in your device settings first.');
    await LocalNotifications.schedule({ notifications: [{
      id: this.nextNotificationId(), title, body, sound: 'default', extra: data,
      ...(Capacitor.getPlatform() === 'android' ? { channelId: 'finflow-updates' } : {}),
      ...(schedule ? { schedule } : {})
    }] });
  }
  async sendGoalReminder(goalName: string, amount: number) {
    await this.scheduleLocalNotification('Goal reminder', `Remember to contribute $${amount} to ${goalName}.`, { type: 'goal_reminder' });
  }
  async sendExpenseAlert(category: string, amount: number) {
    await this.scheduleLocalNotification('Spending alert', `You have spent $${amount} on ${category} this month.`, { type: 'expense_alert' });
  }
  async sendInsightNotification(insight: string) {
    await this.scheduleLocalNotification('Spending insight', insight, { type: 'insight' });
  }
  async sendWeeklyReport() {
    await this.scheduleLocalNotification('Weekly financial review', 'Open FinFlow to review your financial progress.', { type: 'weekly_report' });
  }
  async sendBillReminder(billName: string, dueDate: string) {
    await this.scheduleLocalNotification('Bill reminder', `${billName} is due on ${dueDate}.`, { type: 'bill_reminder' });
  }
  async cancelAllNotifications() {
    const { notifications } = await LocalNotifications.getPending();
    if (notifications.length) await LocalNotifications.cancel({ notifications });
  }
  async getPendingNotifications() { return LocalNotifications.getPending(); }
}
export default NotificationService.getInstance();
